import { emData } from "./visual.jsx";

/**
 * O canal do escritório está fora do ar? — a regra, e só ela.
 *
 * Módulo puro (sem React) porque três superfícies perguntam a mesma coisa: a
 * faixa da casca, o bloco da barra de escrever e o cartão do resumo. Duas contas
 * da mesma coisa é a que envelhece só num dos arquivos — e esta decide se um
 * alarme aparece.
 *
 * Nasceu da queda de 13/09: o aparelho caiu às 16:41 e o escritório descobriu às
 * 18:28, testando à mão. Durante as duas horas a mesa esteve aberta e idêntica à
 * de um dia normal — nada chegava (o que parece cliente calado) e o que saía era
 * gravado com `entrega: "ensaio"`.
 */

/*
 * DUAS perguntas, e misturá-las foi o defeito da primeira versão.
 *
 * "Está conectado?" e "devo gritar?" não são a mesma coisa, e havia uma função
 * só respondendo as duas: como a carência dizia "não está fora", o cartão do
 * resumo concluía "então está conectado" e pintava o ponto verde com o WhatsApp
 * em pleno laço de reconexão. Afirmar conexão que não existe é o pior erro que
 * esta tela pode cometer — ela existe justamente para isso não acontecer.
 *
 * Agora `conectado()` é a verdade, sem carência nenhuma, e a carência mora só no
 * ALARME.
 */

/** Conectado é `conectado`. Nenhum outro estado é, nem por um segundo. */
export const conectado = (wa) => wa?.estado === "conectado";

/** Dá para afirmar alguma coisa? Mesa antiga ou provedor que não carimba: dizer
    "está fora" sem saber é pior que não dizer nada. */
export const sabemos = (wa) => Boolean(wa?.estado);

/*
 * Quanto uma queda precisa durar para virar ALARME, fora o `desligado`.
 *
 * O religamento normal (`esperaDe`, em src/whatsapp/baileys.ts) carimba
 * "conectando" e volta em segundos — faixa vermelha a cada oscilação de rede é o
 * que ensina as pessoas a não ler faixa vermelha. Mas "conectando" também é onde
 * uma queda REAL fica para sempre, porque a espera tem teto de 60s e tenta sem
 * fim: foi o 428 de 13/09, repetindo de minuto em minuto. Sem este relógio, a
 * única queda que apareceria seria a que já matou o pareamento.
 */
export const ESPERA_ATE_AVISAR_MS = 90_000;

/*
 * A carência da BARRA DE ESCREVER é curta, e por outro motivo.
 *
 * Ali a pergunta não é "o canal está com problema", é "o que eu apertar agora
 * vai chegar?" — e durante o laço de reconexão a resposta é não: a mensagem é
 * gravada com `entrega: "ensaio"` e não sai. Oito segundos existem só para não
 * piscar num soluço de rede.
 */
export const ESPERA_DO_COMPOSER_MS = 8_000;

/*
 * Os estados que NÃO esperam relógio: já se sabe que ninguém vai resolver
 * sozinho, então o aviso é imediato.
 *
 * `desligado` é o pareamento morto (precisa de QR). `erro` é o servidor tendo
 * DESISTIDO de tentar (ver `DESISTE_APOS`, em src/whatsapp/baileys.ts) — e é o
 * estado que mais precisa aparecer na hora, porque é o único em que nada mais
 * vai acontecer sem alguém agir. Fazê-lo esperar a carência seria repetir o
 * defeito que a carência existe para evitar.
 */
const PARADOS = new Set(["desligado", "erro"]);

/**
 * Há quanto tempo o estado atual vale, em ms — ou `null` se não dá para saber.
 *
 * `emData` e NÃO `Date.parse`: o banco grava `datetime('now')`, que é UTC sem
 * fuso escrito, e `Date.parse("2026-09-13 22:10:58")` lê isso como hora LOCAL.
 * Em São Paulo (-03) o carimbo virava três horas no FUTURO, a carência nunca
 * vencia, e o cartão anunciava "WhatsApp conectado" com o aparelho fora. É o
 * mesmo tropeço que `avisos.js` já documenta para prazo.
 */
export function haQuantoTempo(wa, agora = Date.now()) {
  const d = emData(wa?.desde);
  if (!d) return null;
  const ms = agora - d.getTime();
  /* Carimbo no futuro (relógio do servidor adiantado) conta como agora, nunca
     como negativo: era o negativo que fazia a carência não vencer nunca. */
  return ms < 0 ? 0 : ms;
}

/**
 * O ALARME: vale a pena interromper quem está trabalhando?
 *
 * `espera` é a carência — 90s na faixa da mesa, 8s na barra de escrever.
 */
export function foraDoAr(wa, agora = Date.now(), espera = ESPERA_ATE_AVISAR_MS) {
  if (!sabemos(wa) || conectado(wa)) return false;
  if (PARADOS.has(wa.estado)) return true;
  const ms = haQuantoTempo(wa, agora);
  /* Sem `desde` a queda é recém-nascida: espera o relógio em vez de gritar. */
  if (ms === null) return false;
  return ms >= espera;
}

/**
 * O motivo, em uma frase, para quem ATENDE.
 *
 * O `detalhe` do servidor já é português e já diz o que fazer ("pareie de novo
 * pelo QR"). O código entra entre parênteses só quando existe: ele não é para
 * quem atende, é para quem vai abrir o chamado — e ter o número na tela evita a
 * ida ao servidor que a queda de 13/09 custou.
 */
export function motivoDoFora(wa) {
  const detalhe = String(wa?.detalhe ?? "").trim();
  const codigo = Number.isFinite(wa?.codigo) ? wa.codigo : null;
  /*
   * Sem detalhe E sem código não há motivo — devolve VAZIO, e não uma paráfrase
   * do título.
   *
   * A frase genérica ("o WhatsApp do escritório está fora do ar") repetia o
   * título da faixa uma palavra depois dele: "WhatsApp do escritório fora do ar
   * · o WhatsApp do escritório está fora do ar". Quem lê duas vezes a mesma
   * coisa conclui que a tela está quebrada, e com razão. Quem chama decide o que
   * fazer com o vazio — ver `temMotivo`.
   */
  if (!detalhe) return codigo ? `o WhatsApp caiu (código ${codigo})` : "";
  return codigo ? `${detalhe} (código ${codigo})` : detalhe;
}

/** "há 2h13" — o tamanho do buraco, que é o que assusta na medida certa. */
export function desdeQuando(wa, agora = Date.now()) {
  const ms = haQuantoTempo(wa, agora);
  if (ms === null) return "";
  const min = Math.floor(ms / 60_000);
  if (min < 2) return "";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  const resto = min % 60;
  return resto ? `há ${h}h${String(resto).padStart(2, "0")}` : `há ${h}h`;
}

/** O número pareado em forma de gente: 554898476638 → +55 (48) 9847-6638. */
export function numeroLegivel(bruto) {
  const so = String(bruto ?? "").replaceAll(/\D/g, "");
  if (so.length < 12) return bruto ?? "";
  const pais = so.slice(0, 2);
  const ddd = so.slice(2, 4);
  const resto = so.slice(4);
  const meio = resto.length > 8 ? resto.length - 4 : 4;
  return `+${pais} (${ddd}) ${resto.slice(0, meio)}-${resto.slice(meio)}`;
}

/*
 * O NÚMERO DO SETOR por onde um caso fala.
 *
 * Um setor pode ter vários números (`wa_conexoes`), e cada caso lembra por qual
 * entrou. `conexao_id` nulo é "fala pelo padrão" — o servidor só grava o vínculo
 * quando ele existe. Resolver aqui, e não em cada tela, é o que faz a linha da
 * lista, o filtro e o cabeçalho da conversa dizerem o MESMO número.
 */
export const numeroDoCaso = (numeros, conexaoId) =>
  (numeros ?? []).find((n) => (conexaoId == null ? n.padrao : n.id === conexaoId)) ?? null;

/** O número em sigla de quatro dígitos ("…4348"), que cabe ao lado de um nome. Sem número, o rótulo. */
export function numeroCurto(n) {
  const so = String(n?.numero ?? "").replaceAll(/\D/g, "");
  return so.length >= 4 ? `…${so.slice(-4)}` : (n?.rotulo ?? "");
}

/*
 * O evento de estado de UM número entrando na tela (`acao: "estado"`).
 *
 * O servidor diz de qual número ele é (`conexao_id`, `padrao`). A linha daquele
 * número em `numeros` muda sempre; o `wa` — o aviso geral da mesa — só quando o
 * evento é do PADRÃO. Parear ou perder o segundo número gritava "WhatsApp fora
 * do ar" para o escritório inteiro com o principal de pé. Evento sem
 * `conexao_id` (servidor antigo) é do padrão, que era o de antes.
 */
export const ehDoPadrao = (ev) => ev?.padrao !== false;

export function comEstadoDoNumero(numeros, ev, agora = new Date().toISOString()) {
  if (ev?.conexao_id == null) return numeros ?? [];
  return (numeros ?? []).map((n) =>
    n.id === ev.conexao_id
      ? { ...n, estado: ev.estado, detalhe: ev.detalhe ?? null, desde: ev.estado === n.estado ? (n.desde ?? null) : agora }
      : n,
  );
}

/**
 * O número da conversa aberta, quando ele NÃO é o padrão — ou `null`.
 *
 * A barra de escrever pergunta "o que eu apertar vai chegar?", e com dois
 * números a resposta é do número DO CASO: com o padrão de pé e o do cliente
 * desligado ela calava; com o padrão caído e o do cliente de pé ela gritava
 * sobre uma mensagem que chega. Para o caso do padrão, quem responde continua
 * sendo o `wa` (mais fresco: o socket vivo tem a última palavra).
 */
export function outroNumeroDoCaso(numeros, conexaoId) {
  if ((numeros?.length ?? 0) < 2 || conexaoId == null) return null;
  const n = numeroDoCaso(numeros, conexaoId);
  return n && !n.padrao ? n : null;
}

/*
 * A lista NOVA de números (evento `whatsapp/conexao`): substitui a inteira, e
 * se o padrão mudou o `wa` — o aviso geral — passa a ser o estado dele. Sem
 * isto, trocar o padrão deixava a mesa falando do número antigo até recarregar.
 * Só os campos de canal mudam no `wa`: provedor e capacidades vêm com a mesa.
 */
export function comListaDeNumeros(atual, numeros) {
  const antes = (atual.numerosDoSetor ?? []).find((n) => n.padrao)?.id;
  const padrao = numeros.find((n) => n.padrao);
  if (!padrao || padrao.id === antes) return { numerosDoSetor: numeros };
  return {
    numerosDoSetor: numeros,
    wa: {
      ...atual.wa,
      estado: padrao.estado,
      detalhe: padrao.detalhe ?? null,
      codigo: null,
      desde: padrao.desde ?? null,
      numero: padrao.numero,
    },
  };
}
